import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material'
import GroupIcon from '@mui/icons-material/Group'
import SendIcon from '@mui/icons-material/Send'
import CancelIcon from '@mui/icons-material/Cancel'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import EmailIcon from '@mui/icons-material/Email'

import ConfirmActionModal from '../../../components/ConfirmActionModal/ConfirmActionModal.component'
import { AppLayoutContext } from '../../../providers/AppLayout'
import AdminTeamService, { AdminInvite, AdminTeamMember } from '../../../services/AdminTeamService/AdminTeamService'
import { useAdminAccess } from '../../../providers/AdminAccessContext'

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

type TeamConfirmAction =
  | { type: 'resend'; inviteId: number; email: string }
  | { type: 'revoke'; inviteId: number; email: string }
  | { type: 'remove'; memberId: string; email: string }

const getConfirmDialogContent = (action: TeamConfirmAction) => {
  switch (action.type) {
    case 'resend':
      return {
        title: 'Resend invitation',
        message: `Send the invitation email again to ${action.email}?`,
        confirmLabel: 'Resend',
      }
    case 'revoke':
      return {
        title: 'Revoke invitation',
        message: `Revoke the pending invitation for ${action.email}? They will not be able to join until invited again.`,
        confirmLabel: 'Revoke',
      }
    case 'remove':
      return {
        title: 'Remove from team',
        message: `Remove ${action.email} from the admin team? They will lose access to this portal.`,
        confirmLabel: 'Remove',
      }
  }
}

const statusChip = (status: AdminInvite['status']) => {
  switch (status) {
    case 'pending':
      return <Chip label="Pending" size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }} />
    case 'accepted':
      return <Chip label="Joined" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }} />
    case 'revoked':
      return <Chip label="Revoked" size="small" sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 600 }} />
    default:
      return <Chip label={status} size="small" />
  }
}

const AdminTeam = (): JSX.Element => {
  const { session, refreshSession } = useAdminAccess()
  const [members, setMembers] = useState<AdminTeamMember[]>([])
  const [invites, setInvites] = useState<AdminInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [email, setEmail] = useState('')
  const [emailBlurred, setEmailBlurred] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | number | null>(null)
  const [confirmAction, setConfirmAction] = useState<TeamConfirmAction | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  AppLayoutContext.setPageName('Team')

  const fetchTeam = useCallback(async (options?: { silent?: boolean }) => {
    if (options?.silent) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    try {
      const data = await AdminTeamService.getTeam()
      setMembers(data.members)
      setInvites(data.invites)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Failed to load team data.'
      setSnackbar({ open: true, message, severity: 'error' })
    } finally {
      if (options?.silent) {
        setRefreshing(false)
      } else {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchTeam()
  }, [fetchTeam])

  const handleInvite = async () => {
    const trimmed = email.trim()
    if (!isValidEmail(trimmed)) {
      return
    }

    setInviting(true)
    try {
      await AdminTeamService.invite(trimmed)
      setEmail('')
      setEmailBlurred(false)
      setSnackbar({
        open: true,
        message: 'Invitation sent. They will get admin access after they sign in to the admin portal.',
        severity: 'success',
      })
      await fetchTeam({ silent: true })
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Failed to send invitation.'
      setSnackbar({ open: true, message, severity: 'error' })
    } finally {
      setInviting(false)
    }
  }

  const handleResend = async (inviteId: number): Promise<boolean> => {
    setActionLoading(inviteId)
    try {
      await AdminTeamService.resendInvite(inviteId)
      setSnackbar({ open: true, message: 'Invitation email resent.', severity: 'success' })
      await fetchTeam({ silent: true })
      return true
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Failed to resend invitation.'
      setSnackbar({ open: true, message, severity: 'error' })
      return false
    } finally {
      setActionLoading(null)
    }
  }

  const handleRevoke = async (inviteId: number): Promise<boolean> => {
    setActionLoading(`revoke-${inviteId}`)
    try {
      await AdminTeamService.revokeInvite(inviteId)
      setSnackbar({ open: true, message: 'Invitation revoked.', severity: 'success' })
      await fetchTeam({ silent: true })
      return true
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Failed to revoke invitation.'
      setSnackbar({ open: true, message, severity: 'error' })
      return false
    } finally {
      setActionLoading(null)
    }
  }

  const handleRemoveMember = async (memberId: string): Promise<boolean> => {
    setActionLoading(memberId)
    try {
      await AdminTeamService.removeMember(memberId)
      setSnackbar({ open: true, message: 'Team member removed.', severity: 'success' })
      await fetchTeam({ silent: true })
      await refreshSession()
      return true
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Failed to remove team member.'
      setSnackbar({ open: true, message, severity: 'error' })
      return false
    } finally {
      setActionLoading(null)
    }
  }

  const handleConfirmAction = async () => {
    if (!confirmAction) {
      return
    }

    const action = confirmAction
    let succeeded = false

    if (action.type === 'resend') {
      succeeded = await handleResend(action.inviteId)
    } else if (action.type === 'revoke') {
      succeeded = await handleRevoke(action.inviteId)
    } else {
      succeeded = await handleRemoveMember(action.memberId)
    }

    if (succeeded) {
      setConfirmAction(null)
    }
  }

  const isConfirmLoading =
    confirmAction?.type === 'resend'
      ? actionLoading === confirmAction.inviteId
      : confirmAction?.type === 'revoke'
        ? actionLoading === `revoke-${confirmAction.inviteId}`
        : confirmAction?.type === 'remove'
          ? actionLoading === confirmAction.memberId
          : false

  const formatDate = (value?: string) => {
    if (!value) return '—'
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const trimmedEmail = email.trim()
  const showEmailError = emailBlurred && trimmedEmail.length > 0 && !isValidEmail(trimmedEmail)
  const canInvite = isValidEmail(trimmedEmail)
  const confirmDialog = confirmAction ? getConfirmDialogContent(confirmAction) : null

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#007C91' }} />
      </Box>
    )
  }

  return (
    <Box>
      <ConfirmActionModal
        open={confirmAction !== null}
        title={confirmDialog?.title ?? ''}
        message={confirmDialog?.message ?? ''}
        confirmLabel={confirmDialog?.confirmLabel ?? 'Confirm'}
        confirmColor={confirmAction?.type === 'resend' ? 'primary' : 'danger'}
        loading={isConfirmLoading}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => void handleConfirmAction()}
      />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#263238', mb: 0.5 }}>
          Team
        </Typography>
        <Typography variant="body2" sx={{ color: '#78909C' }}>
          Invite colleagues to the Scrubstr admin portal. Only invited users can access this app.
        </Typography>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 124, 145, 0.08)' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              fullWidth
              label="Email address"
              placeholder="colleague@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailBlurred(true)}
              type="email"
              size="small"
              error={showEmailError}
              helperText={showEmailError ? 'Enter a valid email address.' : ''}
            />
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleInvite}
              disabled={inviting || !canInvite}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#007C91',
                px: 3,
                py: 1,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#006B7D' },
              }}
            >
              {inviting ? 'Sending…' : 'Send invite'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 124, 145, 0.08)' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <GroupIcon sx={{ color: '#007C91' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238' }}>
              Active members
            </Typography>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ color: '#78909C' }}>No team members yet.</TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>{member.name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell align="right">
                      {member.id === session?.user?.id ? (
                        <Typography variant="caption" sx={{ color: '#78909C' }}>You</Typography>
                      ) : (
                        <Tooltip title="Remove from team">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setConfirmAction({ type: 'remove', memberId: member.id, email: member.email })
                              }
                              disabled={actionLoading === member.id}
                              sx={{ color: '#C62828' }}
                            >
                              {actionLoading === member.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <PersonRemoveIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 124, 145, 0.08)' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <EmailIcon sx={{ color: '#007C91' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238' }}>
              Pending invitations
            </Typography>
            {refreshing && <CircularProgress size={16} sx={{ color: '#007C91' }} />}
          </Stack>
          <Typography variant="body2" sx={{ color: '#78909C', mb: 2 }}>
            People waiting to sign up or accept. Active team members appear above only.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ color: '#78909C' }}>No pending invitations.</TableCell>
                </TableRow>
              ) : (
                invites.map((invite) => (
                  <TableRow key={invite.id}>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell>{statusChip(invite.status)}</TableCell>
                    <TableCell>{formatDate(invite.updatedAt)}</TableCell>
                    <TableCell align="right">
                      {invite.status === 'pending' ? (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Resend invitation">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setConfirmAction({
                                    type: 'resend',
                                    inviteId: invite.id,
                                    email: invite.email,
                                  })
                                }
                                disabled={actionLoading === invite.id}
                                sx={{ color: '#007C91' }}
                              >
                                {actionLoading === invite.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <SendIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Revoke invitation">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setConfirmAction({
                                    type: 'revoke',
                                    inviteId: invite.id,
                                    email: invite.email,
                                  })
                                }
                                disabled={actionLoading === `revoke-${invite.id}`}
                                sx={{ color: '#C62828' }}
                              >
                                {actionLoading === `revoke-${invite.id}` ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <CancelIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#78909C' }}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AdminTeam
